import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import useCommonStore, { ModalConfig } from '@/stores/common';
import { CreateArrayOptions } from '@/constants/constant';
import {
  useGetHotelListQuery,
  useGetProcessedAgentCommissionMutation,
  useUpdateLessonMutation
} from '@/services';
import { toHHmm, toYyyyMmDd } from '@/utils/common';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CommissionApiData,
  ProcessedLessonRow,
  ProcessedLessonUpdateFormValues
} from '@/constants/Interfaces/CommonInterfaces';

/**
 * A11y helper class for visually hidden labels.
 */
const srOnly = 'sr-only';

// Types are imported from common interfaces above to avoid local duplication

import FormGrid from './ProcessedLessonDetailsComponent/FormGrid';
import ActionsBar from './ProcessedLessonDetailsComponent/ActionsBar';

const ProcessedLessonDetailsModal: React.FC = React.memo(() => {
  const { modalConfig = {} as ModalConfig, hideCommonModal } = useCommonStore(
    state => state
  );
  const lesson = useMemo(
    () => ((modalConfig?.data as any)?.lesson || {}) as ProcessedLessonRow,
    [modalConfig?.data]
  );
  const prevCC = (lesson as any)?.financial?.payment_method;
  const headingId = useId();
  const descriptionId = useId();
  // Validation schema
  const formSchema = z
    .object({
      clientName: z.string().trim().min(1, 'Client name is required'),
      am: z.union([z.number().int().min(0, 'AM must be >= 0'), z.literal('')]),
      pm: z.union([z.number().int().min(0, 'PM must be >= 0'), z.literal('')]),
      fd: z.union([z.number().int().min(0, 'FD must be >= 0'), z.literal('')]),
      hotelAgentId: z.union([z.number().int(), z.literal('')]).optional(),
      cc: z.enum(['cash', 'commission'])
    })
    .superRefine((val, ctx) => {
      if (
        val.cc === 'commission' &&
        (val.hotelAgentId === '' || typeof val.hotelAgentId !== 'number')
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Hotel / Agent is required for commission',
          path: ['hotelAgentId']
        });
      }
    });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<ProcessedLessonUpdateFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      clientName: lesson?.client_name ?? '',
      am:
        typeof lesson?.am === 'number'
          ? (lesson.am ?? 0)
          : Number(lesson?.am) || 0,
      pm:
        typeof lesson?.pm === 'number'
          ? (lesson.pm ?? 0)
          : Number(lesson?.pm) || 0,
      fd:
        typeof lesson?.fd === 'number'
          ? (lesson.fd ?? 0)
          : Number(lesson?.fd) || 0,
      hotelAgentId:
        (lesson as any)?.hotel?.id ?? (lesson as any)?.agent?.id ?? '',
      cc: (prevCC as string) === 'cash' ? 'cash' : 'commission'
    }
  });

  const watchedAm = watch('am');
  const watchedPm = watch('pm');
  const watchedFd = watch('fd');
  const watchedHotelAgentId = watch('hotelAgentId');
  const watchedCc = watch('cc');

  const [commissionData, setCommissionData] = useState<CommissionApiData>(null);

  const queryClient = useQueryClient();

  // Fetch hotel and agent lists and combine
  const commonPayload = 'pagination=false';
  const { data: hotelListData } = useGetHotelListQuery(
    `${commonPayload}&type=hotel&sortBy=name&sortOrder=ASC`
  );
  const { data: agentListData } = useGetHotelListQuery(
    `${commonPayload}&type=agent&sortBy=name&sortOrder=ASC`
  );

  const { mutateAsync: fetchAgentCommission } =
    useGetProcessedAgentCommissionMutation();
  const { mutateAsync: updateLesson } = useUpdateLessonMutation();

  // Stable derived date from lesson start
  const lessonDate = useMemo(() => {
    if (lesson?.start) {
      const d = new Date(lesson.start as any);
      if (!isNaN(d.getTime())) return toYyyyMmDd(d.toISOString());
      if (typeof lesson.start === 'string') {
        const m = lesson.start.match(/(\d{4}-\d{2}-\d{2})/);
        if (m) return m[1];
      }
    }
    return toYyyyMmDd(new Date().toISOString());
  }, [lesson?.start]);

  // Debounced commission trigger with argument-deduping
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCommissionArgsRef = useRef<{
    schedule_lesson_id: number;
    agent_id: number;
    lesson_code_id: number;
  } | null>(null);

  const hotelAgentOptions = useMemo(() => {
    const hotels = (
      ((hotelListData as any)?.items ?? []) as Array<{
        id: number;
        name: string;
      }>
    ).map(it => ({ value: it.id, label: it.name }));
    const agents = (
      ((agentListData as any)?.items ?? []) as Array<{
        id: number;
        name: string;
      }>
    ).map(it => ({ value: it.id, label: it.name }));
    return [...hotels, ...agents].sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [hotelListData, agentListData]);

  // Quick lookup set for agent ids
  const agentIdSet = useMemo(() => {
    return new Set(
      (((agentListData as any)?.items ?? []) as Array<{ id: number }>).map(a =>
        String(a.id)
      )
    );
  }, [agentListData]);

  // Quick lookup set for hotel ids
  const hotelIdSet = useMemo(() => {
    return new Set(
      (((hotelListData as any)?.items ?? []) as Array<{ id: number }>).map(h =>
        String(h.id)
      )
    );
  }, [hotelListData]);

  const handleUpdate = useCallback(
    async (values: ProcessedLessonUpdateFormValues) => {
      // Determine selected hotel/agent and map to ids
      const selectedHotelAgent = hotelAgentOptions.find(
        opt => String(opt.value) === String(values.hotelAgentId)
      );

      // Build base payload from current state + lesson
      const basePayload: Record<string, any> = {
        date: lessonDate,
        instructor_id:
          (lesson as any)?.instructor?.id ??
          (lesson as any)?.instructor_id ??
          null,
        string: (lesson as any)?.string ?? '',
        client_name: values.clientName,
        lesson_code_id:
          (lesson as any)?.lesson_code?.id ??
          (lesson as any)?.lesson_code_id ??
          null,
        am: values.am === '' ? 0 : values.am,
        pm: values.pm === '' ? 0 : values.pm,
        fd: values.fd === '' ? 0 : values.fd,
        ski_level: (lesson as any)?.ski_level ?? (lesson as any)?.level ?? null,
        start: toHHmm((lesson as any)?.start),
        end: toHHmm((lesson as any)?.end),
        hotel_id:
          (lesson as any)?.hotel_id ?? (lesson as any)?.hotel?.id ?? null,
        car_id:
          (lesson as any)?.car_id ??
          (lesson as any)?.car?.id ??
          (lesson as any)?.vehicle?.id ??
          null,
        pickup_time: toHHmm((lesson as any)?.pickup_time),
        mountain_id:
          (lesson as any)?.mountain_id ?? (lesson as any)?.mountain?.id ?? null,
        agent_id:
          (lesson as any)?.agent_id ?? (lesson as any)?.agent?.id ?? null,
        reference: (lesson as any)?.reference ?? '',
        paid: 'Y',
        comments: (lesson as any)?.comments ?? '',
        processedUpdate: 'Y',
        payment_method: values.cc
      };

      // Overlay updated fields only
      const updatedFields: Record<string, any> = {};
      if (selectedHotelAgent) {
        // Decide whether the selection is an agent or a hotel, and set accordingly
        const isAgent = agentIdSet.has(String(selectedHotelAgent.value));
        const isHotel = hotelIdSet.has(String(selectedHotelAgent.value));
        if (isAgent) {
          if (
            selectedHotelAgent.value !== basePayload.agent_id ||
            basePayload.hotel_id !== null
          ) {
            updatedFields.agent_id = selectedHotelAgent.value;
            updatedFields.hotel_id = null;
          }
        } else if (isHotel) {
          if (
            selectedHotelAgent.value !== basePayload.hotel_id ||
            basePayload.agent_id !== null
          ) {
            updatedFields.hotel_id = selectedHotelAgent.value;
            updatedFields.agent_id = null;
          }
        }
      } else if (
        (values.hotelAgentId === '' &&
          ((lesson as any)?.hotel_id ?? (lesson as any)?.hotel?.id ?? null) !==
            null) ||
        ((lesson as any)?.agent_id ?? (lesson as any)?.agent?.id ?? null) !==
          null
      ) {
        updatedFields.hotel_id = null;
        updatedFields.agent_id = null;
      }

      // If payment method is cash, force Hotel/Agent to null regardless of selection
      if (values.cc === 'cash') {
        updatedFields.hotel_id = null;
        updatedFields.agent_id = null;
      }

      const finalPayload = { ...basePayload, ...updatedFields };
      const updateId =
        (modalConfig?.data as any)?.lesson?.schedule_group?.id ??
        (modalConfig?.data as any)?.lessonId ??
        (lesson as any)?.schedule_group?.id ??
        (lesson?.id as number);
      // Submit update
      const res = await updateLesson({ id: updateId, ...finalPayload });
      if (res?.success) {
        await queryClient.invalidateQueries({
          queryKey: ['lesson-entries'],
          exact: false
        });
        await queryClient.invalidateQueries({
          queryKey: ['lesson-schedule'],
          exact: false
        });
      }
      hideCommonModal();
    },
    [
      lesson,
      lessonDate,
      modalConfig?.data,
      updateLesson,
      hotelAgentOptions,
      agentIdSet,
      hotelIdSet,
      hideCommonModal,
      queryClient
    ]
  );

  const maybeTriggerCommission = useCallback(
    (overrideAgentId?: number | '') => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(async () => {
        try {
          const candidateAgentId =
            overrideAgentId !== undefined && overrideAgentId !== ''
              ? overrideAgentId
              : agentIdSet.has(String(watchedHotelAgentId))
                ? (watchedHotelAgentId as number)
                : ((lesson as any)?.agent?.id ??
                  (lesson as any)?.agent_id ??
                  '');

          if (candidateAgentId) {
            const args = {
              schedule_lesson_id: Number(lesson?.id ?? 0),
              agent_id: Number(candidateAgentId),
              lesson_code_id: Number(lesson?.lesson_code?.id ?? 0)
            };
            if (
              !lastCommissionArgsRef.current ||
              lastCommissionArgsRef.current.agent_id !== args.agent_id ||
              lastCommissionArgsRef.current.schedule_lesson_id !==
                args.schedule_lesson_id ||
              lastCommissionArgsRef.current.lesson_code_id !==
                args.lesson_code_id
            ) {
              lastCommissionArgsRef.current = args;
              const res = await fetchAgentCommission(args);
              const apiData: CommissionApiData =
                (res as any)?.data?.data ??
                (res as any)?.data ??
                (res as any) ??
                null;
              setCommissionData(apiData);
              // Debug: log commission fetch result
            }
          }
        } catch {
          // ignore
        }
      }, 200);
    },
    [agentIdSet, fetchAgentCommission, watchedHotelAgentId, lesson]
  );

  // Cleanup only: ensure pending debounce is cleared on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Trigger commission fetch on mount when agent preset exists
  useEffect(() => {
    if ((lesson as any)?.agent?.id || (lesson as any)?.agent_id) {
      maybeTriggerCommission(
        (lesson as any)?.agent?.id ?? (lesson as any)?.agent_id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger commission refetch when hotel/agent selection changes
  useEffect(() => {
    void maybeTriggerCommission(watchedHotelAgentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedHotelAgentId]);

  // AM/PM/FD options
  const amOptions = useMemo(() => CreateArrayOptions(100, 'Select AM'), []);
  const pmOptions = useMemo(() => CreateArrayOptions(100, 'Select PM'), []);
  const fdOptions = useMemo(() => CreateArrayOptions(100, 'Select FD'), []);

  const { gross, commissionValue, net } = useMemo(() => {
    const amCount = Number(watchedAm || 0);
    const pmCount = Number(watchedPm || 0);
    const fdCount = Number(watchedFd || 0);
    if (commissionData) {
      // If revenue type is service, map API response directly
      if ((lesson as any)?.lesson_code?.revenue_type === 'service') {
        const g = Number(commissionData.cost || 0);
        const c =
          watchedCc === 'cash' ? 0 : Number(commissionData.commission || 0);
        return { gross: g, commissionValue: c, net: g - c };
      }
      // Participant flow (existing)
      const costAm = Number(commissionData.cost_am || 0);
      const costPm = Number(commissionData.cost_pm || 0);
      const costFd = Number(commissionData.cost_fd || 0);
      const commAm = Number(commissionData.commission_am || 0);
      const commPm = Number(commissionData.commission_pm || 0);
      const commFd = Number(commissionData.commission_fd || 0);

      const g = amCount * costAm + pmCount * costPm + fdCount * costFd;
      const c =
        watchedCc === 'cash'
          ? 0
          : amCount * commAm + pmCount * commPm + fdCount * commFd;
      return { gross: g, commissionValue: c, net: g - c };
    }

    const g = Number(lesson?.gross) || 0;
    const c = watchedCc === 'cash' ? 0 : Number(lesson?.commission) || 0;
    return { gross: g, commissionValue: c, net: g - c };
  }, [watchedAm, watchedPm, watchedFd, commissionData, lesson, watchedCc]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        hideCommonModal();
      }
    },
    [hideCommonModal]
  );

  return (
    <div
      className='p-4 md:p-6'
      role='dialog'
      aria-modal='true'
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      onKeyDown={onKeyDown}
    >
      <h2
        id={headingId}
        className='text-lg md:text-xl font-semibold text-gray-900 mb-4'
      >
        Update
      </h2>
      <p id={descriptionId} className={srOnly}>
        Update processed lesson details such as client, counts, and
        associations.
      </p>

      <div className='w-full overflow-x-auto'>
        <div className='min-w-[720px]'>
          <FormGrid
            control={control}
            errors={errors as any}
            lesson={{ ...lesson, start: toHHmm(lesson?.start ?? '') }}
            amOptions={amOptions}
            pmOptions={pmOptions}
            fdOptions={fdOptions}
            hotelAgentOptions={hotelAgentOptions}
            watchedCc={watchedCc}
            gross={gross}
            commissionValue={commissionValue}
            net={net}
          />
        </div>
      </div>

      <ActionsBar
        onClose={hideCommonModal}
        onSubmit={handleSubmit(values => void handleUpdate(values))}
      />
    </div>
  );
});

export default ProcessedLessonDetailsModal;
